// First-paint fallbacks + first-boot seed source.
//
// Phase 2 of v2 redesign extracted these literal data values out of
// `app/src/config.ts` (now interfaces-only). The DB is canonical at runtime
// via cmsStore. These values fire only:
//   1. On first-ever boot of a fresh DB, when `server/lib/seed.ts` reads
//      them to populate `settings_kv` rows.
//   2. On first-paint of a cold-cache client, before the API response
//      lands. The admin-edited values from the DB supersede them on the
//      next render after hydration.
//
// Keep these in sync with admin-edited shapes (the interfaces in
// `./config.ts`) and with `server/lib/seed.ts`. The drift test in
// `app/src/test/schemas/drift.test.ts` Phase 2.5 catches divergence.

import type {
  SiteConfig,
  NavigationConfig,
  HeroConfig,
  AboutConfig,
  ServicesConfig,
  CTAConfig,
  NewsletterNudgeConfig,
  ExpertiseConfig,
  MethodConfig,
  AudiencePathsConfig,
  BookConfig,
  SeoConfig,
  RouteSeoConfig,
  FooterConfig,
  LegalConfig,
  OffersSectionConfig,
  InsightsSectionConfig,
  LatestInsightsSectionConfig,
  SpeakingSectionConfig,
  SectionHeaderBlob,
  PageBannerConfig,
  WorkshopsPageConfig,
  ProblemGridConfig,
  TopicClustersConfig,
  ContactPageConfig,
  SpeakingEngagement,
  TimelineEntry,
} from './config';

export const siteConfig: SiteConfig = {
  language: "en",
  title: "Chinedu Koggu — Fintech & Digital Infrastructure Strategist",
  description: "Fintech strategist, CTO, and payment systems architect helping African organizations design compliant, scalable payment, automation, and platform systems.",
};

// Spec `navigation.recommended_primary`, in its order. Three notes:
//   - "Three and Company" is deliberately absent: the spec keeps the external
//     link in the footer rather than a priority position ahead of advisory
//     journeys. It is still in footerConfig below.
//   - "Case Studies" labels /portfolio. The spec's nav names /case-studies, but
//     url_preservation says to keep indexed portfolio URLs — so the label moves
//     and the URL does not, with /case-studies redirecting in.
//   - /fractional-cto is gated at render by useLiveTarget: the page seeds
//     unpublished, so the item appears only once the owner approves it.
export const navigationConfig: NavigationConfig = {
  logo: "CK",
  links: [
    { label: "Home", href: "/" },
    { label: "Advisory Services", href: "/services" },
    { label: "Fractional CTO", href: "/fractional-cto" },
    { label: "Case Studies", href: "/portfolio" },
    { label: "Insights", href: "/insights" },
    { label: "Speaking & Workshops", href: "/workshops" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ],
  contactLabel: "Book a Strategic Diagnostic",
  contactHref: "/contact?offer=strategic-diagnostic",
};

export const heroConfig: HeroConfig = {
  visible: true,
  name: "CHINEDU KOGGU",
  // Single role line. Pre-migration this was `roles: [...]`; the first entry
  // is canonical — the second was rarely visible and is dropped intentionally.
  role: "Fintech & Digital Infrastructure Strategist",
  // Headline candidate #1 (active). Stashed for future A/B:
  //   #2: "Practical infrastructure for African finance. Built quietly, built for context."
  //   #3: "Better payment systems. Built for how African markets actually work."
  headline: "Designing systems for the markets we have — not the markets we wish for.",
  description:
    "Twenty years building compliant payment systems, automation platforms, and the implementation roadmaps that survive contact with real institutions.",
  primaryCtaLabel: "Start a Systems Review →",
  // Carries the offer so the site's most prominent CTA records attribution —
  // a bare /contact tells the CRM nothing about which link produced the lead.
  primaryCtaHref: "/contact?offer=strategic-diagnostic",
  secondaryCtaLabel: "Explore Advisory Services",
  secondaryCtaHref: "/services",
  positioningLine: "Build in silence. Ship what's real.",
  backgroundImage: "/images/portrait-office-2.jpg",
  // Tunable layout defaults — admin can override per-deploy without code edits.
  headlineMaxWidth: 560,
  // 30% keeps the full head visible on the 1024×1280 source portrait; lower
  // values shift the face higher in the frame.
  portraitObjectPositionY: "30%",
  // Hero v4 — dual-mode + scrim + tunable lens.
  heroMode: "lens",
  backgroundPositionX: 75,
  backgroundBlurPx: 12,
  overlayEnabled: true,
  overlayDirection: "left-right",
  overlayOpacityLight: 85,
  overlayOpacityDark: 75,
  overlayColorLight: "#FFFFFF",
  overlayColorDark: "#000000",
  textColorLight: "#0A0A0A",
  textColorDark: "#F5F5F5",
  // Lens window — face is centered horizontally in the source portrait
  // (so X=50). Y=35 matches portraitObjectPositionY so the lens lands on
  // the face out of the box. W=H=28 vw keeps the prior square footprint.
  lensPositionXPercent: 50,
  lensPositionYPercent: 35,
  lensWidthPercent: 28,
  lensHeightPercent: 28,
  // Background image transform. Defaults mirror the legacy
  // `backgroundPositionX: 75` so freshly seeded rows look identical to the
  // pre-feature output; admins now have explicit Y + scale knobs.
  bgImageOffsetX: 75,
  bgImageOffsetY: 30,
  bgImageScale: 1,
};

export const aboutConfig: AboutConfig = {
  label: "ABOUT",
  description: "I help African organizations turn complex payment, automation, and platform problems into clear architectures, practical roadmaps, and commercially useful systems. My work sits at the intersection of payments, technology, compliance, automation, and business model design — translating between the people who fund, regulate, build, and use the systems we ship.",
  experienceValue: "20+",
  experienceLabel: "Years in\nFintech",
  portraitImage: "/images/portrait-office-1.jpg",
  quote: "I don't just build products — I design systems of freedom. Technology that creates dignity, infrastructure that enables innovation, and leadership rooted in ethics.",
  tags: ['Payment Architecture', 'Digital Transformation', 'Startup Mentorship', 'Platform Strategy'],
  stats: [
    { value: "20+ years", label: "Building digital infrastructure" },
    { value: "5M+", label: "VISA/Mastercard cards deployed" },
    { value: "60+", label: "Verified projects shipped" },
    { value: "11", label: "African Bank Affiliates Live" },
  ],
  images: [
    { src: "/images/about-1.jpg", alt: "Modern fintech office in Lusaka" },
    { src: "/images/about-2.jpg", alt: "Mobile payment innovation" },
    { src: "/images/about-3.jpg", alt: "Lusaka cityscape" },
    { src: "/images/about-4.jpg", alt: "Tech mentorship session" },
  ],
};

// Career timeline fallback used by `sections/Experience.tsx` when the
// `experience` DB collection is empty (fresh install or admin emptied the
// table). The canonical store is the `experience` table — this list is
// purely a first-paint / empty-state safety net. Not part of `aboutConfig`
// since the timeline data moved out of the `about_config` settings_kv blob
// into its own collection.
export const defaultTimeline: TimelineEntry[] = [
  { year: '2001', title: 'B.Eng. from FUTO', description: 'Graduated with B.Eng. Electrical Electronics Engineering (Electronics & Computer Engineering) from the Federal University of Technology Owerri, Nigeria.' },
  { year: 'Pre-2010', title: 'VISA & MasterCard at Unified Payments / ValuCard', description: 'Led massive implementations of VISA and MasterCard projects reaching over 5 million cards across Africa.' },
  { year: '2010', title: 'Co-Founded ProBASE Limited', description: 'Began a 14-year run as Co-Founder & CTO, architecting multi-rail platforms, collections systems, and card-issuing platforms for major banks, governments, and telecom operators in Zambia.' },
  { year: '2013', title: 'Partner at C2M Networks Ltd', description: 'Joined C2M Networks as Partner (Business Solutions) — ongoing engagement alongside other ventures.' },
  { year: '2018', title: 'AFRICATELLING storyteller', description: 'Began contributing narratives and thought leadership on African tech and business experience.' },
  { year: '2019', title: 'Alibaba eFounder Fellowship', description: 'Selected for the prestigious programme by Alibaba Business School and UNCTAD, training African entrepreneurs as catalysts for digital transformation.' },
  { year: '2023', title: 'CTO at TechMasters Zambia', description: 'Took on the CTO role at TechMasters Zambia Limited, focused on platform development, process automation, and innovative business solution design. Active coach and mentor at BongoHive.' },
];

export const servicesConfig: ServicesConfig = {
  label: "SERVICES",
  heading: "Advisory pillars for clear, compliant digital infrastructure.",
  services: [
    {
      iconName: "Cpu",
      title: "Payment System Architecture",
      description: "Multi-rail platforms that connect banks, mobile networks, and government systems into a single, reliable transaction layer. Built to handle national scale from day one.",
      image: "/images/domain-architecture.jpg",
      deliverables: [
        "Architecture assessment and technology roadmap",
        "Vendor evaluation framework",
        "Implementation oversight and quality assurance",
        "Regulatory compliance review",
      ],
      bestFor: "Banks, fintechs, and telecoms building or modernising payment infrastructure",
    },
    {
      iconName: "CreditCard",
      title: "Card & Digital Wallet Infrastructure",
      description: "End-to-end card issuing and acquiring systems — from EMV chip personalization to real-time authorization. Experience deploying to millions of cardholders across African markets.",
      image: "/images/domain-fintech.jpg",
      deliverables: [
        "Card scheme design and issuer processor selection",
        "Digital wallet architecture and KYC flow design",
        "Interoperability and network connectivity strategy",
      ],
      bestFor: "Issuers, wallet providers, and payment aggregators",
    },
    {
      iconName: "Compass",
      title: "Digital Transformation Strategy",
      description: "Helping enterprises replace legacy processes with automated, cloud-native workflows. Practical roadmaps that account for organizational reality, not just technology potential.",
      image: "/images/domain-strategy.jpg",
      deliverables: [
        "Current state assessment and gap analysis",
        "Digital strategy and implementation roadmap",
        "Technology partner selection and governance framework",
      ],
      bestFor: "Traditional financial institutions and telecoms entering digital financial services",
    },
    {
      iconName: "Mic",
      title: "Mentorship & Knowledge Sharing",
      description: "Coaching founders at BongoHive on product-market fit and business model design. Speaking at tech events about what actually works — and what doesn't — when building in Africa.",
      image: "/images/domain-speaking.jpg",
      deliverables: [
        "Structured 1:1 mentorship programme",
        "Team training and capability building workshops",
        "Advisory support for FinTech cohorts and accelerators",
      ],
      bestFor: "FinTech accelerators, innovation hubs, schools, and individuals entering the FinTech space",
    },
  ],
};

export const portfolioConfig = {
  projects: [
    { slug: 'zambia-premier-league-ticketing-platform', title: 'Zambia Premier League Ticketing Platform', tag: 'E-Commerce', outcome: 'Jan 2026 – Present', associatedWith: 'TechMasters Zambia Limited', description: 'The Zambia Premier League Ticketing Platform for the Zambia Premier League – providing cashless ticket purchase for ZPL (Approved by the Football Association of Zambia).' },
    { slug: 'mobile-money-integration-to-pos-project-for-fnb-zambia', title: 'Mobile Money Integration to POS Project for FNB Zambia', tag: 'Mobile Money', outcome: 'Oct 2025 – Present', associatedWith: 'TechMasters Zambia Limited', description: 'Worked with the team to facilitate the FNB POS application for processing Mobile Money Payments as well as integration to both Airtel and FNB systems.' },
    { slug: 'payeasy-platform', title: 'PayEasy Platform', tag: 'Payments', outcome: 'Jan 2024 – Present', associatedWith: 'TechMasters Zambia Limited', description: 'Third Party Bill Payments and Collections Platform operated by TechMasters for companies looking for simplified payment collections for events, bills, etc.' },
    { slug: 'techpay-platform', title: 'TechPay Platform', tag: 'Payments', outcome: 'May 2023 – Present', associatedWith: 'TechMasters Zambia Limited', description: 'Payment aggregation platform for integration of merchants that supports mobile money and online card payments via MPGS gateway.' },
    { slug: 'enapsa-module-within-smartpay', title: 'eNAPSA Module within SmartPAY', tag: 'Govtech', outcome: 'Jun 2017 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'Integrate the eNAPSA module for pensions payments into the SmartPAY platform across six (6) client banks. The list is gradually expanding as more clients integrate into the SmartPAY platform.' },
    { slug: 'enapsa-portal-for-napsa-zambia', title: 'eNAPSA Portal for NAPSA Zambia', tag: 'Govtech', outcome: 'Jun 2016 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'The National Pension Scheme Authority (NAPSA) engaged ProBase through a competitive bid process to deliver an electronic platform to facilitate the submission and electronic payments of returns by employers of labour…' },
    { slug: 'implementation-of-the-etax-platform-smartpay-for-cavmont-bank', title: 'Implementation of the eTAX Platform (SMARTPAY) for Cavmont Bank', tag: 'Tax Collections', outcome: 'May 2015 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'Project delivered at Probase Limited (Zambia).' },
    { slug: 'consultant-for-mquid-on-electronic-payment-solutions', title: 'Consultant for MQuid on Electronic Payment Solutions', tag: 'Advisory', outcome: 'Feb 2015 – Present', associatedWith: 'C2M Networks Ltd', description: 'Providing insight, knowledge and expertise for electronic payment systems and ways to implement for small financial institutions.' },
    { slug: 'deployment-of-e-statement-generation-platform-for-fbz', title: 'Deployment of E-Statement Generation Platform for FBZ', tag: 'Banking', outcome: 'Jan 2015 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'The bank required an E-statement platform or module to generate monthly bank statements for their clients which is sent out via their email system.' },
    { slug: 'stanbic-bank-zambia-elma-mobile-banking-platform', title: 'Stanbic Bank Zambia – ELMA Mobile Banking Platform', tag: 'Mobile Banking', outcome: 'Jul 2014 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'ELMA Mobile Banking Platform.' },
    { slug: 'stanbic-mobile-money-project', title: 'Stanbic Mobile Money Project', tag: 'Mobile Money', outcome: 'Mar 2014 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'Stanbic is deploying a robust mobile money platform to expand its products and services to both the banked and unbanked segments of the population. This solution is agnostic and open-ended and will allow integration…' },
    { slug: 'smartpay-etax-system-for-standard-chartered-bank-zambia', title: 'SMARTPAY – ETAX SYSTEM for Standard Chartered Bank Zambia', tag: 'Tax Collections', outcome: 'Dec 2013 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'The Zambian Revenue Authority (ZRA) in a bid to automate tax payments collections country wide has deployed a tax system ASCUDAWORLD to alleviate tax payments and collection challenges. Probase developed the relevant…' },
    { slug: 'smartpay-etax-system-for-stanbic-bank-zambia', title: 'SMARTPAY – ETAX SYSTEM for Stanbic Bank Zambia', tag: 'Tax Collections', outcome: 'Dec 2013 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'The Zambian Revenue Authority (ZRA) in a bid to automate tax payments collections country wide has deployed a tax system ASCUDAWORLD to alleviate tax payments and collection challenges. Probase developed the relevant…' },
    { slug: 'kowric-crowd-funding-sourcing-platform', title: 'Kowric Crowd Funding & Sourcing Platform', tag: 'E-Commerce', outcome: 'Sep 2013 – Present', associatedWith: 'Kowric', description: 'An online platform to facilitate crowd-funding and sourcing using the power of the collective. Started with a partner strength of eight persons who have volunteered their time and energy to the process. The platform…' },
    { slug: 'security-training', title: 'Security Training', tag: 'Advisory', outcome: 'Dec 2012 – Present', associatedWith: 'C2M Networks Ltd', description: 'Delivering security awareness training covering Basic Security Awareness, Awareness Training (Basics and Literacy), Role-based Training, and Education-level training. The program builds knowledge, skills, and…' },
    { slug: 'credit-card-issuance-for-access-bank-nigeria', title: 'Credit Card Issuance – For Access Bank Nigeria', tag: 'Card Infrastructure', outcome: 'Nov 2012 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'Access Bank now issues Naira credit cards using the Transware card management system. Features include multi-currency financial schemes, individual credit limits under corporate contracts, authorization corporate…' },
    { slug: 'ecobank-instant-issuance-for-visa-classic-debit', title: 'ECOBANK Instant Issuance for Visa Classic Debit', tag: 'Card Infrastructure', outcome: 'Sep 2012 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'Ecobank initiated mass issuance from their existing VISA Classic Debit BIN. The project provided pre-printed plastic cards issued to customers, with onsite coordination to provide logistic and platform support during…' },
    { slug: 'banklink-eft-platform-for-banks', title: 'BankLink EFT Platform for Banks', tag: 'Banking', outcome: 'Apr 2012 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'An internal bank transfer platform used by banks to facilitate transfers through the DDAC transfer window with four sessions.' },
    { slug: 'visa-electron-issuing-project-for-diamond-bank-international-use', title: 'Visa Electron Issuing Project for Diamond Bank – International Use', tag: 'Card Infrastructure', outcome: 'Apr 2012 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'Diamond Bank requested a VISA international card for use outside Nigeria. The key requirement was attaching local (naira) accounts to the card, allowing customers to draw value from existing accounts without opening…' },
    { slug: 'access-bank-nigeria-co-branded-visa-debit-card-project', title: 'Access Bank Nigeria Co-Branded Visa Debit Card Project', tag: 'Card Infrastructure', outcome: 'Jan 2012 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'The bank pursued segmentation of their existing BIN for the Visa Classic Debit program targeting companies, educational institutions, and other business segments. Gradually unrolling a strategy for maximum utilization…' },
    { slug: 'contactless-card-certification-and-deployment', title: 'Contactless Card Certification and Deployment', tag: 'Card Infrastructure', outcome: 'Jan 2012 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'Deploying Contactless for customer banks in the form of VISA payWave – involving card certification, personalization of dual interface cards, and POS testing.' },
    { slug: 'integrated-mobile-money-solution', title: 'Integrated Mobile Money Solution', tag: 'Mobile Money', outcome: 'Jan 2012 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'Working on the new frontier in the payment space – various models and integration to current infrastructure involving banks, financial institutions, third party processors, payment systems (VISA, MasterCard…' },
    { slug: 'visa-credit-card-issuing-project-for-access-bank-nigeria', title: 'Visa Credit Card Issuing Project for Access Bank Nigeria', tag: 'Card Infrastructure', outcome: 'Oct 2011 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'The Visa credit card issuing project for Access Bank Nigeria utilizing a revolving credit billing scheme that allocates an overdraft with a repayment plan for discerning customers, facilitating quicker transactions…' },
    { slug: 'visa-prepaid-debit-issuing-project-for-access-bank-nigeria', title: 'Visa Prepaid Debit Issuing Project for Access Bank Nigeria', tag: 'Card Infrastructure', outcome: 'Oct 2011 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'The prepaid program implemented for Access Bank and their customers – a reloadable over-the-counter issued debit card with a virtual account setup to facilitate spending and increase customer spending.' },
    { slug: 'bulk-sms-messaging-and-integration', title: 'Bulk SMS Messaging and Integration', tag: 'Messaging', outcome: 'Aug 2010 – Present', associatedWith: 'Probase Limited (Zambia)', description: 'Providing bulk SMS messaging capabilities and integration for various institutions such as banks, insurance companies, churches, government establishments, and others. Involved in designing and deploying the…' },
    { slug: 'visa-issuance-and-acquiring-project-for-uba-african-affiliates', title: 'VISA Issuance and Acquiring Project for UBA African Affiliates', tag: 'Card Infrastructure', outcome: 'Jan 2010 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'Setting up the issuing leg for thirteen UBA Affiliates in Africa, including project provisioning with VISA, forms completion, VPA profiling, Issuer Public Key generation and loading, white plastic certification, final…' },
    { slug: 'upgrades-for-card-management-platform', title: 'Upgrades for Card Management Platform', tag: 'Card Infrastructure', outcome: 'Sep 2007 – Present', associatedWith: 'Unified Payment Services Ltd', description: 'Managing a constant upgrade cycle with at least two major upgrades per year (April and October, coinciding with VISA\'s bi-annual upgrade cycles). Also handling upgrades for other payment systems like MasterCard and…' },
    { slug: 'national-road-fund-agency-e-toll-platform', title: 'National Road Fund Agency e-Toll Platform', tag: 'Govtech', outcome: 'Sep 2017 – Apr 2023', associatedWith: 'Probase Limited (Zambia)', description: 'A government-approved project to facilitate electronic tolling and gradually replace cash with electronic toll cards that can be loaded with e-value, providing better revenue assurance and reducing income leakages.…' },
    { slug: 'ab-bank-sms-integration', title: 'AB Bank – SMS Integration', tag: 'Messaging', outcome: '2021 – 2021', associatedWith: 'Probase Limited (Zambia)', description: 'SMS integration for client transactional alerts and notifications.' },
    { slug: 'ab-bank-wallet-to-bank-integration', title: 'AB Bank – Wallet to Bank Integration', tag: 'Mobile Money', outcome: '2021 – 2021', associatedWith: 'Probase Limited (Zambia)', description: 'AB Bank wallet and USSD offering integration with mobile money.' },
    { slug: 'napsa-bus-operator-and-marketeer-enrolment-system-bome', title: 'NAPSA – Bus Operator and Marketeer Enrolment System (BOME)', tag: 'Govtech', outcome: '2020 – 2021', associatedWith: 'Probase Limited (Zambia)', description: 'Informal sector platform for bus operator and marketeer enrollment as part of NAPSA\'s informal sector strategy to unlock and provide value and increase informal sector participation.' },
    { slug: 'national-health-insurance-enhima-employer-and-client-portal', title: 'National Health Insurance – eNHIMA Employer and Client Portal', tag: 'Govtech', outcome: 'Jun 2020 – Dec 2020', associatedWith: 'Probase Limited (Zambia)', description: 'National portal for mandatory health insurance. The NHI Act seeks to promote Universal Health Coverage through a continuum of care encompassing health promotion, prevention of disease, quality curative services, and…' },
    { slug: 'zicb-core-banking-and-surround-systems-roll-out', title: 'ZICB Core Banking and Surround Systems Roll Out', tag: 'Core Banking', outcome: 'Apr 2018 – Dec 2020', associatedWith: 'Probase Limited (Zambia)', description: 'ProBASE was awarded a tender to facilitate the deployment and rollout of the technical setup of the new bank, including project management and oversight, infrastructure and software capabilities, as well as core…' },
    { slug: 'revamped-probase-sms-platform', title: 'Revamped ProBASE SMS Platform', tag: 'Messaging', outcome: 'Jan 2019 – Aug 2019', associatedWith: 'Probase Limited (Zambia)', description: 'SMS aggregation platform with API and Bulk SMS capabilities for domestic and international traffic.' },
    { slug: 'focus-insurance-e-commerce-platform', title: 'Focus Insurance e-Commerce Platform', tag: 'E-Commerce', outcome: 'Jul 2016 – Mar 2017', associatedWith: 'Probase Limited (Zambia)', description: 'An online e-commerce powered insurance portal used to facilitate electronic payments via debit/credit cards for FOCUS Group.' },
    { slug: 'deployment-of-smartpay-etax-platform-for-uba', title: 'Deployment of SmartPAY eTAX Platform for UBA', tag: 'Tax Collections', outcome: 'Oct 2016 – Feb 2017', associatedWith: 'Probase Limited (Zambia)', description: 'ProBase completed the deployment of its premier flagship product SmartPAY, utilized for payment collections of Customs and Domestic Taxes.' },
    { slug: 'fnb-zambia-tax-online-platform', title: 'FNB Zambia Tax Online Platform', tag: 'Tax Collections', outcome: 'Oct 2016 – Nov 2016', associatedWith: 'Probase Limited (Zambia)', description: 'ProBase was engaged by FNB Zambia to deploy the Domestic Tax Online portal to cater to customers paying domestic taxes via FNB Internet Banking. The solution facilitates a redirection from the ZRA portal to the bank\'s…' },
    { slug: 'isurvey-data-capture-portal', title: 'iSurvey Data Capture Portal', tag: 'Data & Survey', outcome: 'Jan 2016 – Jun 2016', associatedWith: 'C2M Networks Ltd', description: 'A survey and data capture solution designed to assist an NGO with field visits to low-income earners for gathering and monitoring health programs and initiatives. The platform has facilitated data gathering in various…' },
    { slug: 'implementation-and-deployment-of-smartpay-for-banc-abc-towards-tax-collections', title: 'Implementation and Deployment of SMARTPAY for Banc ABC towards Tax Collections', tag: 'Tax Collections', outcome: 'May 2015 – Nov 2015', associatedWith: 'Probase Limited (Zambia)', description: 'Probase developed the platform interface and web services to allow integration between Banc ABC\'s systems and the ZRA (ASCUDAWORLD) platform, catering to both Import/Export Taxes at various ports and Domestic Taxes.…' },
    { slug: 'gis-and-branch-monitoring-portal-for-grooming-center', title: 'GIS and Branch Monitoring Portal for Grooming Center', tag: 'Data & Survey', outcome: 'Mar 2015 – Sep 2015', associatedWith: 'C2M Networks Ltd', description: 'A GIS platform to track and monitor branch activity and provide real-time feedback across over 560 Grooming branches in Nigeria. Features a management dashboard and MIS portal for reporting and data management, with…' },
    { slug: 'messaging-api-for-bulk-sms', title: 'Messaging API for Bulk SMS', tag: 'Messaging', outcome: 'Aug 2014 – Oct 2014', associatedWith: 'Probase Limited (Zambia)', description: 'Created a consolidated service fabric to seamlessly integrate and service various clients offering messaging and notification services via a single API with background connections to multiple providers. Implemented…' },
    { slug: 'branded-sms-portal-for-seedco-zambia', title: 'Branded SMS Portal for SeedCo Zambia', tag: 'Messaging', outcome: 'Jun 2014 – Sep 2014', associatedWith: 'Probase Limited (Zambia)', description: 'Project manager and system design analyst overseeing the specification, design, and implementation. Co-ordinated the project team that developed and tested the system.' },
    { slug: 'e-statement-generation-notification-for-access-bank-zambia', title: 'e-Statement Generation & Notification for Access Bank Zambia', tag: 'Banking', outcome: 'Jun 2014 – Aug 2014', associatedWith: 'Probase Limited (Zambia)', description: 'Based on a submitted specification and scope of work from Access Bank Zambia, ProBase undertook the design, development, and implementation of a solution for generating and emailing customer account statements at…' },
    { slug: 'custom-sms-portal-for-stanbic-bank', title: 'Custom SMS Portal for Stanbic Bank', tag: 'Messaging', outcome: 'May 2014 – Jun 2014', associatedWith: 'Probase Limited (Zambia)', description: 'Project manager for the development and deployment of an SMS service portal used internally by Stanbic Bank to engage external and internal client groups. The portal is hosted internally and provides features…' },
    { slug: 'customer-engagement-mobile-application', title: 'Customer Engagement Mobile Application', tag: 'Mobile App', outcome: 'Jan 2013 – Dec 2013', associatedWith: 'C2M Networks Ltd', description: 'A mobile engagement application for the Android platform to cultivate user interaction and engagement for brand awareness and advertising. Brings together users, brands, and actionable information using the mobile…' },
    { slug: 'data-capture-and-collection-project-for-grooming-center', title: 'Data Capture and Collection Project for Grooming Center', tag: 'Data & Survey', outcome: 'Mar 2013 – Nov 2013', associatedWith: 'C2M Networks Ltd', description: 'The data capture of customer forms into a pre-defined database, run at preset intervals every year to present electronic records of the client base.' },
    { slug: 'currency-re-basing-for-banking-clients-in-zambia', title: 'Currency Re-basing for Banking Clients in Zambia', tag: 'Card Infrastructure', outcome: 'Dec 2012 – Dec 2012', associatedWith: 'Unified Payment Services Ltd', description: 'The project involved setup of various platforms for the country\'s currency (Zambia) re-basing exercise stipulated to take place at the end of December 2012. Required updating currency codes across all platforms for…' },
    { slug: 'currency-re-basing-for-uba-zambia', title: 'Currency Re-basing for UBA Zambia', tag: 'Card Infrastructure', outcome: 'Dec 2012 – Dec 2012', associatedWith: 'Unified Payment Services Ltd', description: 'The project involved setup of the various platforms for the purpose of the country\'s currency (Zambia) re-basing exercise at the end of December 2012. Required updating currency codes across all issuing and acquiring…' },
    { slug: 'access-bank-sierra-leone-visa-classic-debit-issuance-project', title: 'Access Bank Sierra Leone Visa Classic Debit Issuance Project', tag: 'Card Infrastructure', outcome: 'Apr 2012 – Sep 2012', associatedWith: 'Unified Payment Services Ltd', description: 'Co-ordinated the issuance project for Access Bank Sierra Leone. Went onsite to set up, train, and complete issuance operations. The bank is now able to issue Visa Debit Cards to existing customers for local and…' },
    { slug: 'access-bank-sierra-leone-visa-classic-card-issuance-and-implementation-project', title: 'Access Bank Sierra Leone Visa Classic Card Issuance and Implementation Project', tag: 'Card Infrastructure', outcome: 'Jan 2012 – Aug 2012', associatedWith: 'Unified Payment Services Ltd', description: 'Under the Access Bank Group, the issuance and implementation project for Access Bank Sierra Leone to commence card issuance and acquiring operations. Concluded the issuance leg of the project with the bank…' },
    { slug: 'ecobank-legacy-account-migration-project', title: 'EcoBank Legacy Account Migration Project', tag: 'Banking', outcome: 'Jun 2012 – Jul 2012', associatedWith: 'Unified Payment Services Ltd', description: 'Following EcoBank\'s acquisition of Oceanic Bank, a migration of legacy Oceanic Bank accounts to the EcoBank format on the card management system and switch was required. The project spanned over one month and…' },
    { slug: 'fidelity-visa-gold-debit-issuance-and-acquiring-programme', title: 'Fidelity Visa Gold Debit Issuance and Acquiring Programme', tag: 'Card Infrastructure', outcome: 'Nov 2011 – Jun 2012', associatedWith: 'Unified Payment Services Ltd', description: 'Fidelity Bank PLC expanded their VISA product stable by issuing VISA Gold cards to their customers, adopting Unified Payments as their third-party processor. Project is now completed.' },
    { slug: 'visa-issuing-project-access-bank-sierra-leone', title: 'VISA Issuing Project – Access Bank Sierra Leone', tag: 'Card Infrastructure', outcome: 'Jun 2012 – Jun 2012', associatedWith: 'Unified Payment Services Ltd', description: 'Enabled Access Bank Sierra Leone as a VISA issuer. Responsibilities included host-to-host testing preparatory to go-live, terminal maintenances on CBA, pilot run co-ordination, trace log review, CBA issue resolution…' },
    { slug: 'visa-gold-debit-issuing-project-for-access-bank-nigeria', title: 'Visa Gold Debit Issuing Project for Access Bank Nigeria', tag: 'Card Infrastructure', outcome: 'Oct 2011 – Feb 2012', associatedWith: 'Unified Payment Services Ltd', description: 'A new standard product deployment for a debit card issuance operation targeting the bank\'s high net-worth customers. Compliments the classic debit card programme as an alternative spending instrument.' },
    { slug: 'visa-platinum-debit-issuing-project-for-access-bank-nigeria', title: 'Visa Platinum Debit Issuing Project for Access Bank Nigeria', tag: 'Card Infrastructure', outcome: 'Oct 2011 – Feb 2012', associatedWith: 'Unified Payment Services Ltd', description: 'Product deployment for debit card issuance targeting very high net-worth customers. Actively involved from inception to conclusion. Product went live and was undergoing user acceptance testing at the bank.' },
    { slug: 'eleven-uba-african-affiliates-visa-issuing-and-acquiring-project', title: 'Eleven UBA African Affiliates – VISA Issuing and Acquiring Project', tag: 'Card Infrastructure', outcome: 'Jan 2008 – Dec 2011', associatedWith: 'Unified Payment Services Ltd', description: 'UBA created more than ten VISA issuing and acquiring projects over a two-year span, sometimes running concurrently. ValuCard (now Unified Payments) was tasked as issuer and acquirer processor for the affiliates. The…' },
    { slug: 'nuban-bank-migration-exercise', title: 'NUBAN Bank Migration Exercise', tag: 'Banking', outcome: 'Jan 2011 – Dec 2011', associatedWith: 'Unified Payment Services Ltd', description: 'A mandatory project by the Central Bank of Nigeria to standardise the account number structure across all financial institutions to a ten-digit standard. Involved in the migration process for several Nigerian banks…' },
    { slug: 'first-bank-verve-card-pin-mailer-printing-project', title: 'First Bank Verve Card Pin Mailer Printing Project', tag: 'Card Infrastructure', outcome: 'Nov 2011 – Nov 2011', associatedWith: 'Unified Payment Services Ltd', description: 'A project at the instance of First Bank requiring a total backlog of almost 200,000 Pin mailers to be printed and dispatched in less than one week. The target was achieved, earning the company almost twenty million…' },
    { slug: 'access-bank-zambia-visa-classic-card-issuance-and-implementation-project', title: 'Access Bank Zambia Visa Classic Card Issuance and Implementation Project', tag: 'Card Infrastructure', outcome: 'Jun 2009 – Jun 2009', associatedWith: 'Unified Payment Services Ltd', description: 'Designated personnel sent to assist the Access Zambia team in their Visa Classic Card programme implementation. Set up the card management system on site, conducted training, assisted with VISA certification testing…' },
    { slug: 'ecommerce-merchant-integration', title: 'Ecommerce Merchant Integration', tag: 'E-Commerce', outcome: 'Sep 2006 – Dec 2008', associatedWith: 'Unified Payment Services Ltd', description: 'Initial role as e-commerce specialist enabling merchant integration for the payment portal. Involved merchant registration, due diligence, and testing to ensure compliance. Provided code and integration assistance…' },
  ],
};

export const testimonialsConfig = {
  testimonials: [
    {
      name: "Mbamwabi Chilembo",
      title: "Energy Project Development & Regulatory Compliance",
      quote:
        "Chinedu is a visionary and a great leader. His deep industry insights and experience have allowed him to be a pioneer in his field. Working with him in financial services was an absolute pleasure.",
      image: "",
    },
    {
      name: "Francis Mumbi (MSc)",
      title: "Head, Data and Analytics | Strategy, Finance, Technology, Applied AI & Robotics",
      quote:
        "I worked with Chinedu; he demonstrated strong leadership, tenacity and drive.",
      image: "",
    },
    {
      name: "Okafor Enete",
      title: "Director, Principal MDM Architect — Data & Analytics at LTM",
      quote:
        "A highly motivated IT professional with an extensive skill set in integration, database management, e-payment systems, consultancy and delivery. One highlight of his core competence: he led the team at Valucard where we rolled out over 5 million Visa and MasterCard. Chinedu is the guy you want on your team.",
      image: "",
    },
    {
      name: "Chibale Chimambo",
      title: "Tech Innovation",
      quote:
        "I worked with Chinedu on one of the biggest mobile banking projects at Stanbic Bank. He is a very diligent manager of people and resources — instrumental both as a technical manager and as a vendor in ensuring requirements were closely followed through. A very hard-working person.",
      image: "",
    },
    {
      name: "Oreofe Oyewole",
      title: "ISO 27001 LI | Cybersecurity, Privacy, Compliance, Payments",
      quote:
        "A very detailed and meticulous individual, highly skilled and exceptional in electronic card issuance matters and related projects. Dependable and always willing to assist colleagues and customers with difficult tasks. I would definitely recommend him for any card management project — he will surpass expectations.",
      image: "",
    },
    {
      name: "Chris N. Anazia",
      title: "Founder, FinTech Risk & Cyber GRC Leader | Digital Payments, Card Systems, PCI DSS",
      quote:
        "Chinedu is a hardworking and humble fellow with the skill set of an IT person and a service provider — ensuring work goes on seamlessly and challenges are resolved on time. He and his then-supervisor Francis Ibok worked tirelessly and played key roles as service provider in ensuring Visa card issuance and operations at Access and Skye Bank were successful.",
      image: "",
    },
    {
      name: "Alexander Ositadimma Obi",
      title: "Unified Payments Services Limited",
      quote:
        "Very analytical, detail-oriented, structured and organised with a keen sense of managing projects to meet their objectives. His deep knowledge of Visa card issuers and project implementation cannot be overemphasised. He regularly demonstrates an ability to think through issues and provide practical solutions — always willing to help and carry others along.",
      image: "",
    },
    {
      name: "Nonso Ezekwere",
      title: "Project Manager | Release Manager | Card & Payments Professional",
      quote:
        "Working with Chinedu on several e-payment projects has been very stimulating — he has a way of pushing the team to think beyond the usual. A professional par excellence with profound analytical ability. Chinedu comes highly recommended.",
      image: "",
    },
    {
      name: "Engr. Mohammed Gujbawu, FNICE MNSE",
      title: "Deputy Manager, Nigerian Nuclear Regulatory Authority",
      quote:
        "A great person to work with — one of the most dedicated staff I have ever come across. His passion for the job is exceptional. A team player that is always ready to learn from his subordinates. He has an exceptional multitasking ability with a reliable level of accuracy. I will recommend Chinedu to any company or organization requiring his services.",
      image: "",
    },
    {
      name: "Artem Ivanenko",
      title: "Global Learning & Talent Development Leader | Gallup-Certified Strengths Coach",
      quote:
        "I really enjoyed having Chinedu as a trainee at TranzWare Academy in Russia and Nigeria. He demonstrated outstanding curiosity and passion for learning. Although the products and systems we covered were very complex, his learning curve was steep and he always asked the 'right' questions — going above and beyond the standard curriculum.",
      image: "",
    },
    {
      name: "Kafui Dey",
      title: "Broadcaster & Producer",
      quote:
        "Chinedu is a proactive team player. He interacts effectively with colleagues, subordinates and supervisors, and is willing to go the extra mile to ensure that results are achieved.",
      image: "",
    },
    {
      name: "Ojoma Ochai",
      title: "Managing Director, CcHUB Africa | Board Member",
      quote:
        "Chinedu facilitated an Africa-wide workshop for us in Ethiopia, and feedback from participants and the lead facilitator was excellent.",
      image: "",
    },
    {
      name: "Lydia Martin",
      title: "Citizens Rights | Partnerships | Project Management",
      quote:
        "Chinedu was a valued member of the team, acting as a facilitator on an arts project for the British Council. He was thorough and approachable and brought humour and creativity to the role and the project as a whole.",
      image: "",
    },
    {
      name: "Ase-Olodumare Opasanya",
      title: "Network Consulting Engineer at Cisco",
      quote:
        "Talk of innovative thinking — Chinedu brings it to bear in his work. Thorough, always keeping an eye on the objective, ensuring what is done today has an impact on the end results. A self-starter who habitually exceeds set targets.",
      image: "",
    },
    {
      name: "Catherine Mwaba Chali",
      title: "Chief Operating Officer, United Bank for Africa Zambia",
      quote:
        "Chinedu is an excellent person to work with — very professional and reliable, willing to go an extra mile to serve customers. Hiring him would be a plus to any company.",
      image: "",
    },
    {
      name: "Samson Idakwo",
      title: "Technical Manager",
      quote:
        "Without a doubt the most enthusiastic, motivating, creative and knowledgeable e-payment and customer-experience professional I have ever had the honour to work with. His talent for getting the best out of every team member is exceptional, and his strategic outlook on e-commerce is simply unparalleled. Everyone who came into contact with him walked away a far better professional.",
      image: "",
    },
    {
      name: "James Aikhionbare",
      title: "Risk and Compliance Analyst",
      quote:
        "Chinedu has shown exemplary skills in the discharge of his duties. Not only is he meticulous in carrying out tasks, he has also been a mentor to his co-workers — imparting knowledge that has been of great help. A team player, humble, and a gentleman to the core. I strongly recommend him.",
      image: "",
    },
    {
      name: "Imafidon Austine Oziegbe",
      title: "AI Professional",
      quote:
        "Chinedu is a dynamic one-out-of-a-million professional. I met him when I started my career in electronic banking and consulted him for four years on behalf of my bank. Get in touch with him with your problems or new ideas and he will bring them to life. He is a solution bank — gives attention to details, listens, and knows his job like the palm of his hand. 10/10 anytime.",
      image: "",
    },
    {
      name: "Olanrewaju Akeem",
      title: "Network Administrator, Central Securities and Clearing System",
      quote:
        "An excellent team player with unique skills and an extraordinary patience for resolving issues. Highly dedicated, never compromises on the quality of his work, and delivers exactly the results expected.",
      image: "",
    },
    {
      name: "Emmanuel Ejembi, DBA, PMP®",
      title: "Chief Technology Advisor | Enterprise Technology Leader",
      quote:
        "Chinedu is very thoughtful and detailed — and can be easily relied upon. I have no hesitation whatsoever in recommending him to whoever requires his services.",
      image: "",
    },
    {
      name: "Sam Obafemi",
      title: "Founder/CEO, Cerca Africa | Business Ecosystem Designer",
      quote:
        "Chinedu is a detailed candidate for any goal. He never misses the chance to show concern for colleagues and friends. On the job, results are his motivation — he needs no supervision and delivers in time. I admire his towering personality.",
      image: "",
    },
    {
      name: "Daniel Eze",
      title: "Senior Solutions Architect | Business Process Automation | ISO 27001 Lead Auditor",
      quote:
        "An honest, hardworking achiever — ardent researcher, dedicated to the core. Smart, honest, and very reliable; always gets the job done despite the odds. I would not hesitate to recommend him to anyone who needs a loyal, dedicated person.",
      image: "",
    },
  ] as Array<{ name: string; title: string; quote: string; image: string }>,
};

export const speakingConfig = {
  topics: [
    'FinTech in Africa',
    'Payment Infrastructure & Interoperability',
    'Digital Transformation Strategy',
    'Card & Wallet Innovation',
    'Innovation Leadership',
    'Mentorship at Scale',
  ],
  engagements: [
    { event: 'PMI Zambia Conference 2025', role: 'Speaker', year: '2025' },
    { event: '2025 Banking & Finance Conference (ZIBFS)', role: 'Speaker', year: '2025' },
    { event: 'ICA Zambia 2025 — Innovation Cafe', role: 'Panelist', year: '2025' },
    { event: '28th Annual ZIM Conference', role: 'Speaker', year: '2025' },
    { event: 'Flutterwave Zambia Re-Launch Fintech Mixer', role: 'Panelist', year: '2025' },
    { event: 'Beyond Chat: Revolutionising Business with AI', role: 'Moderator', year: '2024' },
    { event: 'Zambia E-commerce Expo and Conference', role: 'Panelist', year: '2023' },
    { event: 'Zambia Impact Investment Summit', role: 'Moderator & Facilitator', year: '2023' },
    { event: 'Innovation Africa 2022 — Ministerial Summit', role: 'Delegate', year: '2022' },
    { event: 'BongoHive Fintech Challenge Pitch Day', role: 'Judge', year: '2021' },
    { event: 'BongoHive Podcast, S2 E6', role: 'Guest Panelist', year: '2021' },
    { event: 'BongoHive Facebook Live', role: 'Featured Speaker', year: '2020' },
    { event: 'BongoHive — Alibaba eFounders Session', role: 'Speaker', year: '2019' },
    { event: 'Alibaba eFounders Fellowship — Class 3: Africa', role: 'Fellow', year: '2018' },
    { event: 'DataHack4FI Season 2 Launch', role: 'Panelist', year: '2018' },
    { event: 'British Council "Footprints" Workshop', role: 'Facilitator', year: '2008' },
  ] as SpeakingEngagement[],
};

// NOTE: No longer consumed by CTA.tsx (Task 10 rewrote CTA to 3-column layout).
// Kept for backward compat in case admin CMS references it.
export const ctaConfig: CTAConfig = {
  tags: ["Payment Systems", "Fintech Compliance", "Platform Strategy", "Fractional CTO"],
  heading: "Start with clarity, not code.",
  description: "If your organization is building a payment platform, automation system, digital product, or regulated technology service, start with a strategic diagnostic. Ninety minutes to surface the real problem, the hidden risks, and the path forward.",
  buttonText: "Book a Strategic Diagnostic",
  buttonHref: "/contact",
  email: "hello@chinedukoggu.com",
  backgroundImage: "",
};

export const newsletterNudge: NewsletterNudgeConfig = {
  enabled: true,
  heading: "Get new insights first",
  body: "No spam. Unsubscribe anytime.",
  ctaLabel: "Subscribe",
};

// ─── Brand realignment fallbacks ─────────────────────────────────
// These are seed defaults used by `useConfig` when the API call hasn't
// resolved yet. Live values come from `settings_kv` via /api/config/<key>
// and the admin CMS — fallbacks only render on the very first paint of a
// fresh install before the API responds. Keep these in sync with
// `server/lib/seed.ts` brand defaults.

export const expertiseConfig: ExpertiseConfig = {
  labels: [
    "Fintech Strategist",
    "Payment Systems Architect",
    "Platform Builder",
    "Fractional CTO",
    "Regulatory & Risk Translator",
    "Automation & AI Advisor",
    "Mentor & Systems Thinker",
  ],
};

export const methodConfig: MethodConfig = {
  label: "THE CLEAR SYSTEMS METHOD",
  heading: "A practical method for turning unclear systems into structured platforms.",
  intro:
    "Before architecture, before code, before a roadmap — there has to be clarity. The CLEAR Systems Method is the discipline I bring to every engagement: a five-step pass that turns a fragmented idea, payment system, or platform concept into something teams can actually build.",
  frameworkName: "CLEAR Systems Method",
  steps: [
    { key: "C", title: "Clarify the problem", description: "Name what the system actually is, who it serves, and what it is meant to do. Strip away decoration and assumptions until the real problem is on the table." },
    { key: "L", title: "Locate users, money, risks, and constraints", description: "Map the people, transaction flows, regulatory pressure points, operational constraints, and dependencies that the system has to live with." },
    { key: "E", title: "Engineer the architecture and operating model", description: "Design the technical architecture and the operating model together — including responsibilities, ownership, and the day-two operations that keep it alive." },
    { key: "A", title: "Align compliance, business, and technical requirements", description: "Reconcile what regulators, partners, and finance expect with what engineering can ship — before commitments harden and rework gets expensive." },
    { key: "R", title: "Roadmap the build, governance, and growth path", description: "Sequence the build with clear milestones, governance checkpoints, and a growth path that survives delivery contact with the real world." },
  ],
};

// Homepage "What I help solve" grid — spec `homepage.problem_grid`.
//
// The spec supplies six LABELS and nothing else: no descriptions, no links. The
// copy below is authored, and so is every destination.
//
// Each tile carries TWO hrefs. `href` is where it should point once the
// specialist page is published; `fallbackHref` is where it points until then,
// and is always a route that resolves. Without that pairing the grid would
// render two tiles out of six at launch — a section that looks broken — or six
// tiles, four of which 404.
//
// Both halves keep attribution: a `?offer=` fallback still records leads.cta_id
// (Phase 1.1). What upgrades on publish is the destination, not the tracking.
//
// Two topics map to services that already exist, so they need no page at all.
export const problemGrid: ProblemGridConfig = {
  label: "WHAT I HELP SOLVE",
  heading: "Six problems that bring people here.",
  visible: true,
  items: [
    {
      iconName: "Network",
      title: "Payment systems architecture",
      description:
        "Rails, settlement, reconciliation, and the integration map — designed before the build commits you to it.",
      fallbackHref: "/services#payment-systems-architecture",
    },
    {
      iconName: "Compass",
      title: "Fractional technology leadership",
      description:
        "Senior technology judgment in the room when the decision gets made, without a full-time executive hire.",
      href: "/fractional-cto",
      fallbackHref: "/contact?offer=fractional-cto-retainer",
    },
    {
      iconName: "Globe2",
      title: "Payments market entry",
      description:
        "Rails, partners, operating model, and launch risks for a new African market — while they are still cheap to change.",
      href: "/payments-market-entry",
      fallbackHref: "/contact?offer=payments-market-entry",
    },
    {
      iconName: "SearchCheck",
      title: "Technical due diligence",
      description:
        "An independent read on technology, product, payments, and delivery risk for investors, boards, and acquirers.",
      href: "/technical-due-diligence",
      fallbackHref: "/contact?offer=technical-due-diligence",
    },
    {
      iconName: "Landmark",
      title: "Government digital collections",
      description:
        "How citizens and businesses actually pay for taxes, levies, and services — journey, integration, reconciliation, adoption.",
      href: "/government-digital-collections",
      fallbackHref: "/contact?offer=government-digital-collections",
    },
    {
      iconName: "Bot",
      title: "AI and automation",
      description:
        "Where automation removes friction and improves control — and where it decorates a broken process and adds risk.",
      fallbackHref: "/services#automation-ai-readiness",
    },
  ],
};

export const audiencePathsConfig: AudiencePathsConfig = {
  label: "WHO I WORK WITH",
  heading: "Choose the path that fits where you are.",
  paths: [
    {
      iconName: "Building2",
      title: "I lead an institution that needs clarity before it builds",
      description: "For banks, PSPs, insurers, telcos, and public-sector teams designing payment, compliance, or platform infrastructure.",
      ctaLabel: "Start a Systems Review",
      ctaHref: "/contact?path=institution",
    },
    {
      iconName: "Rocket",
      title: "I am a founder building a regulated platform",
      description: "For founders and growth-stage ventures shaping a fintech, marketplace, or automation platform that has to survive regulation, capital, and African operating reality.",
      ctaLabel: "Book a Strategic Diagnostic",
      ctaHref: "/contact?path=founder",
    },
    {
      iconName: "BookOpen",
      title: "I want to read, learn, or be mentored",
      description: "For product teams, accelerators, students, and operators looking for essays, case notes, masterclasses, or structured mentorship.",
      ctaLabel: "Explore Insights",
      ctaHref: "/insights",
    },
    {
      iconName: "LineChart",
      title: "I am assessing a platform before I commit capital",
      description:
        "For investment committees, boards, and acquirers who need an independent read on technology, product, payments, and delivery risk.",
      ctaLabel: "Request a Diligence Discussion",
      ctaHref: "/contact?offer=technical-due-diligence",
    },
    {
      iconName: "Landmark",
      title: "I run a public institution digitising how people pay",
      description:
        "For revenue authorities, pension funds, and agencies improving collections, adoption, and reconciliation for taxes, levies, and fees.",
      ctaLabel: "Discuss a Collections Programme",
      ctaHref: "/contact?offer=government-digital-collections",
    },
    {
      iconName: "Compass",
      title: "I need senior technology judgment without an executive hire",
      description:
        "For CEOs and founders who need architecture, vendor, roadmap, and delivery-governance decisions owned by someone who has made them before.",
      ctaLabel: "Discuss Fractional Advisory",
      ctaHref: "/contact?offer=fractional-cto-retainer",
    },
  ],
};

/** Insight hubs at `/insights/topics/<slug>` — spec §"topic hubs" (line 204).
 *
 *  `tags` are the article tag strings verbatim; they are what routes an article
 *  into a hub. A cluster with no published articles is hidden from the browse
 *  row and 404s, so `digital-public-infrastructure` and `ai-automation` stay
 *  dark until the owner publishes into them.
 *
 *  Titles and descriptions are authored — the spec supplies no `seo.pages`
 *  entry for any hub. Kept topical and claim-free per criterion #4.
 */
export const topicClusters: TopicClustersConfig = {
  label: "BROWSE BY TOPIC",
  heading: "Insights by topic.",
  visible: true,
  clusters: [
    {
      slug: "payments",
      name: "Payments",
      title: "Payment Infrastructure Insights — Rails & Settlement",
      description:
        "Essays on payment rails, settlement and reconciliation design, and the integration decisions that shape how a payment platform gets built and run.",
      tags: ["Payment Infrastructure Notes"],
      ctaHref: "/payments-market-entry",
    },
    {
      slug: "fintech-operations",
      name: "Fintech Operations",
      title: "Fintech Operations Insights — Controls & Platforms",
      description:
        "Writing on fintech operating controls, platform design, and the strategy behind exceptions, partner readiness, and the day-to-day work of delivery.",
      tags: ["African Fintech Strategy", "Platform Thinking"],
      ctaHref: "/fractional-cto",
    },
    {
      slug: "african-market-entry",
      name: "African Market Entry",
      title: "African Market Entry Insights — Payments & Realities",
      description:
        "Notes on operating models, partner dependencies, and the market realities that decide whether a payments or fintech launch lands in African markets.",
      tags: ["African Market Realities"],
      ctaHref: "/payments-market-entry",
    },
    {
      slug: "digital-public-infrastructure",
      name: "Digital Public Infrastructure",
      title: "Digital Public Infrastructure Insights — Collections",
      description:
        "Writing on digital public collections, payment journeys, reconciliation, and the controls public institutions need in place before a wider rollout.",
      tags: [],
      ctaHref: "/government-digital-collections",
    },
    {
      slug: "ai-automation",
      name: "AI & Automation",
      title: "AI & Automation Insights for Financial Operations Teams",
      description:
        "Where automation genuinely helps financial and payment operations, where it quietly adds risk, and how to judge which processes are ready for it.",
      tags: [],
      ctaHref: "/contact?offer=ai-automation-review",
    },
    {
      slug: "leadership",
      name: "Leadership",
      title: "Leadership Insights — Founder and Product Operating Notes",
      description:
        "Operating lessons for founders and product leaders: owning technology decisions, building teams, and spotting the failure patterns that keep repeating.",
      tags: ["Founder and Product Notes"],
      ctaHref: "/fractional-cto",
    },
  ],
};

export const bookConfig: BookConfig = {
  enabled: false,
  title: "",
  coverImage: "",
  description: "",
  ctaLabel: "",
  ctaHref: "",
};

export const seoConfig: SeoConfig = {
  ogTitle: "Chinedu Koggu — Fintech & Digital Infrastructure Strategist",
  ogDescription:
    "I help banks, fintechs, PSPs, insurers, SMEs, and public-sector institutions design compliant payment systems, automation platforms, and implementation roadmaps that work in real African operating conditions.",
  ogImage: "/images/portrait-office-2.jpg",
  ogUrl: "https://chinedukoggu.com/",
  twitterCard: "summary_large_image",
  twitterSite: "@chinedukoggu",
  jsonLd: {
    name: "Chinedu Chigozie Patrick Rapheal Koggu",
    alternateName: "Chinedu Koggu",
    jobTitle: "Fintech & Digital Infrastructure Strategist",
    description:
      "Fintech strategist, CTO, and payment systems architect helping African organizations design compliant, scalable payment, automation, and platform systems.",
    url: "https://chinedukoggu.com/",
    sameAs: [
      "https://www.linkedin.com/in/chinedukoggu/",
      "https://www.f6s.com/member/chinedukoggu",
      "https://about.me/chinedu.koggu",
    ],
    serviceTypes: [
      "Payment Systems Architecture",
      "Fintech Compliance Advisory",
      "Platform Strategy",
      "Fractional CTO Advisory",
    ],
    areaServed: "Africa",
  },
};

// Per-route titles + descriptions. Before this existed, every route served the
// site-wide ogTitle/ogDescription to crawlers and social scrapers, so /services
// and /contact advertised the homepage. Descriptions describe what is already
// on each page — no new factual claims.
export const routeSeo: RouteSeoConfig = {
  routes: [
    {
      path: "/",
      title: "Chinedu Koggu — Payments & Digital Infrastructure Advisory",
      description:
        "Payments architecture, fractional CTO advisory, fintech strategy, and digital infrastructure guidance for organisations building in African markets.",
    },
    {
      path: "/about",
      title: "About — Chinedu Koggu",
      description:
        "Two decades across payment systems, fintech compliance, and platform delivery — the background, timeline, and credentials behind the advisory work.",
    },
    {
      path: "/services",
      title: "Payments, Fintech & Fractional CTO Advisory Services",
      description:
        "Productized advisory for payment systems, fintech readiness, platform strategy, automation, and senior technology decision support.",
    },
    {
      path: "/portfolio",
      title: "Case Studies — Payment, Platform & Govtech Work",
      description:
        "Selected projects across payment rails, cards and wallets, mobile money, public-sector collections, and platform builds.",
    },
    {
      path: "/insights",
      title: "Insights on Payments, Fintech Operations & African Markets",
      description:
        "Essays and case notes on payment architecture, settlement and reconciliation, fintech operating controls, and building in African markets.",
    },
    {
      path: "/workshops",
      title: "Speaking & Workshops on Payments, Fintech and AI",
      description:
        "Talks, panels, and working sessions on payment systems, platform design, fintech operations, and practical automation for teams and leaders.",
    },
    {
      path: "/contact",
      title: "Contact — Start a Strategic Diagnostic",
      description:
        "Tell me the decision, risk, or opportunity you are working through and which engagement fits. Every enquiry reaches me directly.",
    },
    {
      path: "/privacy",
      title: "Privacy Policy — Chinedu Koggu",
      description:
        "What this site collects when you use the contact form or chat widget, how it is used, how long it is kept, and how to request deletion.",
    },
  ],
};

export const footerConfig: FooterConfig = {
  logo: "CK",
  description: "Chinedu Koggu — fintech strategist, payment systems architect, and digital infrastructure thinker focused on African markets.",
  columns: [
    {
      title: "Navigation",
      links: [
        { label: "About", href: "/about" },
        { label: "Advisory Services", href: "/services" },
        { label: "Case Studies", href: "/portfolio" },
        { label: "Insights", href: "/insights" },
        { label: "Speaking & Workshops", href: "/workshops" },
        { label: "Contact", href: "/contact" },
      ],
    },
    {
      title: "Engage",
      links: [
        { label: "Strategic Diagnostic", href: "/contact?offer=strategic-diagnostic" },
        { label: "Architecture Blueprint", href: "/contact?offer=architecture-blueprint" },
        { label: "Fractional CTO Retainer", href: "/contact?offer=fractional-cto-retainer" },
      ],
    },
    {
      title: "Connect",
      links: [
        { label: "LinkedIn", href: "https://www.linkedin.com/in/chinedukoggu/" },
        { label: "F6S Profile", href: "https://www.f6s.com/member/chinedukoggu" },
        { label: "about.me", href: "https://about.me/chinedu.koggu" },
        { label: "Schedule a Call", href: "/contact" },
      ],
    },
  ],
  socialLinks: [
    { iconName: "Linkedin", href: "https://www.linkedin.com/in/chinedukoggu/", label: "LinkedIn" },
    { iconName: "X", href: "https://x.com/chinedukoggu", label: "X" },
    { iconName: "Globe", href: "https://about.me/chinedu.koggu", label: "about.me" },
    { iconName: "ExternalLink", href: "https://www.f6s.com/member/chinedukoggu", label: "F6S" },
  ],
  copyright: `© ${new Date().getFullYear()} Chinedu Koggu. All rights reserved.`,
  credit: "Designed with purpose.",
};

export const legalConfig: LegalConfig = {
  visible: false,
  heading: "Privacy Policy",
  body: "",
};

export const offersSection: OffersSectionConfig = {
  heading: "How to work with me.",
  intro: "Five productized offers — each with a clear purpose, output list, and CTA. Pick the one that fits where you are.",
  visible: true,
};

export const insightsSection: InsightsSectionConfig = {
  heading: "Insights",
  intro: "Thinking on FinTech, payments, digital transformation, and leadership.",
  visible: true,
};

export const latestInsightsSection: LatestInsightsSectionConfig = {
  eyebrow: "INSIGHTS",
  heading: "Latest Insights",
  subheading: "Thinking on FinTech, payments, and leadership.",
  ctaLabel: "Read all insights",
  visible: true,
};

// Section-header blobs for collection-driven Home sections. The heading
// strings here are what was previously hardcoded as JSX `|| 'Fallback'` —
// now seeded into settings_kv so admins see them in SectionCopyPanel.
export const projectsSection: SectionHeaderBlob = {
  heading: "Systems Built to Last",
  visible: true,
};
export const testimonialsSection: SectionHeaderBlob = {
  heading: "What people say",
  visible: true,
};
export const experienceSection: SectionHeaderBlob = {
  heading: "Two Decades of Building",
  visible: true,
};
export const certificationsSection: SectionHeaderBlob = {
  heading: "Earned, Not Claimed",
  visible: true,
};
export const thoughtLeadershipSection: SectionHeaderBlob = {
  heading: "Thinking in Public",
  visible: true,
};

export const speakingSection: SpeakingSectionConfig = {
  visible: true,
  podcastLabel: "PODCAST",
  podcastName: "Three & Company",
  podcastDescription:
    "Co-hosted with George Mwale, Three & Company is a conversation about building in Africa — the messy middle between idea and impact. We talk to founders, operators, and thinkers who are doing the work, not just talking about it.",
  podcastTopics:
    "Topics span personal growth, entrepreneurship, and the practical realities of building businesses that matter on the continent.",
  podcastPlatforms: [{ name: "YouTube" }, { name: "Facebook" }],
  speakingLabel: "SPEAKING",
  speakingName: "On Stage",
  speakingDescription:
    "I speak at events where the audience wants substance over spectacle. Whether it is a technical deep-dive on payment architecture or a broader conversation about Africa's digital trajectory, I bring two decades of hands-on experience to the stage.",
  ctaLabel: "Invite Me to Speak",
  // Was "#contact" — an anchor to an element that exists on no page rendering
  // this section, so the CTA was inert (audit L-A7). Existing installs are
  // healed by migrateNavigationOnce; this default covers fresh ones.
  ctaHref: "/contact?type=Speaking",
};

// Page banners — H1 + intro for full-route top-of-page surfaces. Item lists
// (services, projects, speaking events) live in their own DB tables; these
// blobs only carry the page-level header copy + visibility flag.

export const servicesPage: PageBannerConfig = {
  heading: "Services",
  intro: "Advisory pillars covering payment systems architecture, fintech compliance, platform strategy, fractional CTO advisory, automation and AI readiness, technical documentation, and mentorship — designed for African operating reality.",
  visible: true,
};

export const portfolioPage: PageBannerConfig = {
  heading: "Portfolio",
  intro: "Selected projects and engagements across payment infrastructure and digital transformation.",
  visible: true,
};

// /workshops. Renamed from /speaking, which now 301s here.
//
// The spec asks this page to "publish topics, formats, and audience outcomes"
// and supplies the four format LABELS only — duration, audience and outcome
// below are authored. Facilitation claims are unchanged: the speaking events
// collection and the Three & Company block are the owner's own, and the spec's
// rule is to retain verified facilitation claims exactly as sourced.
export const speakingPage: WorkshopsPageConfig = {
  heading: "Speaking & Workshops",
  intro:
    "Talks, panels, and working sessions on payment systems, platform design, fintech operations, market entry, and practical automation — for teams that want substance over spectacle.",
  visible: true,
  formats: [
    {
      name: "Executive briefing",
      duration: "60–90 minutes",
      audience: "Boards, executive teams, and investment committees",
      outcome:
        "A shared, accurate picture of how a payment or platform decision actually works, and what it commits the organisation to.",
    },
    {
      name: "Half-day working session",
      duration: "Half day",
      audience: "Product, engineering, and operations leads",
      outcome:
        "A specific decision worked through end to end — rails, partners, controls — with the trade-offs written down.",
    },
    {
      name: "Full-day design workshop",
      duration: "Full day",
      audience: "Delivery teams and cross-functional programme groups",
      outcome:
        "A drafted target architecture or operating model the team owns, plus the risk register that comes with it.",
    },
    {
      name: "Founder clinic / masterclass",
      duration: "By arrangement",
      audience: "Founders, accelerators, and innovation hubs",
      outcome:
        "Direct, unhedged answers on architecture, regulation, and sequencing from someone who has shipped in these markets.",
    },
  ],
  topics: [
    "Payment settlement and reconciliation design",
    "Card, mobile money, bank, and gateway integration decisions",
    "Sponsor-bank and partner readiness",
    "Fintech operating controls, refunds, chargebacks, and exceptions",
    "How to evaluate a payment-platform or fintech architecture",
    "Digital public collections and reconciliation",
    "Market-entry operating models in African markets",
    "Where AI and automation help financial operations — and where they create risk",
  ],
};

export const contactPage: ContactPageConfig = {
  heading: "Let's Work Together",
  intro: "Tell me about what you are working on. I will respond within 48 hours.",
  visible: true,
  directContactLine: "Or reach me directly: LinkedIn · hello@chinedukoggu.com",
  formLabels: {
    name: "Name",
    email: "Email",
    organisation: "Organisation",
    // Bare label — the Contact.tsx renderer appends "(optional)" itself for
    // non-required fields. Persisted blobs that still carry the trailing
    // " (optional)" suffix get healed by migrateContactPageOnce.
    phone: "Phone",
    type: "What do you want to discuss?",
    role: "Role",
    organisationType: "Organisation type",
    countryOrMarkets: "Country or markets",
    desiredTiming: "Timing",
    consent: "I agree to my details being used to respond to this enquiry.",
    message: "What decision, risk, or opportunity are you working through?",
    submit: "Send request",
  },
  formPlaceholders: {
    name: "Full name",
    email: "Work email",
    organisation: "Company / institution",
    phone: "Include your country code",
    role: "e.g. Head of Payments",
    countryOrMarkets: "e.g. Zambia, or West Africa",
    desiredTiming: "e.g. this quarter",
    message: "Brief description of the decision, risk, or opportunity you are working through...",
  },
  // Engagement/service-interest options. The Contact page prepends the live
  // `offers` collection to this list, so these are the fallback + catch-alls.
  engagementTypes: ["Consulting", "Speaking", "Mentorship", "Other"],
  organisationTypes: [
    "Bank/PSP",
    "Fintech",
    "Founder/Platform",
    "Investor/Board",
    "Public Sector",
    "Innovation/Training",
    "Other",
  ],
  privacyLinkLabel: "Privacy policy",
};
